"use client";

import Circular from "@/components/Circular";
import CircularButton from "@/components/CircularButton";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import BorderedInputDatePicker from "@/components/DatePicker/styled/BorderedInputDatePicker";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import LoadingAnimation from "@/components/LoadingAnimation";
import Select from "@/components/Select";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { AccountType } from "@knowt/syncing/graphql/schema";
import noop from "@knowt/syncing/utils/noop";
import TextField from "@mui/material/TextField";
import Zoom from "@mui/material/Zoom";
import { Check, CircleAlert, Eye, EyeOff } from "lucide-react";
import { HTMLInputTypeAttribute, useEffect, useRef, useState } from "react";

export const AuthInputWithLabel = ({
    label,
    style,
    errors = [],
    type,
    onChange,
    ...inputProps
}: {
    label: string;
    type: HTMLInputTypeAttribute;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    errors?: (string | null | undefined)[];
} & React.InputHTMLAttributes<HTMLInputElement>) => {
    const hasErrors = errors?.filter(Boolean)?.length;

    const [isFocus, setIsFocus] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const EyeIcon = passwordVisible ? EyeOff : Eye;

    return (
        <FlexColumn>
            <label
                htmlFor={label}
                style={{
                    fontSize: "1.4rem",
                    fontWeight: "600",
                    lineHeight: "2.4rem",
                    marginBottom: "1rem",
                    color: hasErrors ? themeColors.errorPrimary : themeColors.neutralBlack,
                }}>
                {hasErrors ? errors[0] : label}
            </label>
            <FlexRowAlignCenter style={{ position: "relative" }}>
                <TextField
                    variant="filled"
                    onChange={onChange}
                    type={type === "password" ? (passwordVisible ? "text" : "password") : type}
                    id={label}
                    style={{ flex: 1, border: "none", ...style }}
                    onFocus={() => setIsFocus(true)}
                    onBlur={() => setIsFocus(false)}
                    sx={{
                        borderRadius: isFocus ? "0rem" : "2rem",
                        overflow: "hidden",
                        "&& .MuiInputBase-root": {
                            "::after": {
                                borderBottomColor: themeColors.primary,
                            },
                            "::before": {
                                borderBottomColor: "transparent",
                            },
                            ":hover::before": {
                                borderBottomColor: "transparent",
                            },
                        },
                        "&& .MuiFilledInput-root": {
                            "::before": {
                                borderBottomColor: "transparent",
                            },
                        },
                    }}
                    inputProps={{
                        ...inputProps,
                        style: {
                            fontWeight: "600",
                            fontFamily: "var(--knowt-font-name)",
                            fontSize: "1.5rem",
                            padding: "0rem 1.6rem",
                            height: "4rem",
                            backgroundColor: isFocus ? themeColors.neutralWhite : themeColors.background,
                            color: themeColors.neutralBlack,
                            outline: hasErrors ? `2px solid ${themeColors.errorPrimary}` : "none",
                            flex: 1,
                        },
                    }}
                />
                {type === "password" && (
                    <CircularButton
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        type="button"
                        radius="2.5rem"
                        sx={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            backgroundColor: "transparent",
                            right: "1.2rem",
                        }}>
                        <EyeIcon strokeWidth={strokeWidth.normal} size={iconSizes.SM} />
                    </CircularButton>
                )}
            </FlexRowAlignCenter>
        </FlexColumn>
    );
};

export const AuthInputCode = ({
    length,
    onChange,
    style,
    anyCharacter = false,
    defaultValue,
    ...inputProps
}: {
    length: number;
    onChange: (newCode: string) => void;
    anyCharacter?: boolean;
} & Omit<React.HTMLAttributes<HTMLInputElement>, "onChange" | "onKeyDown">) => {
    const ref = useRef<HTMLInputElement[]>([]);
    const [code, setCode] = useState<string[]>(() => {
        if (defaultValue) {
            return new Array(length).fill("").map((_, i) => defaultValue[i]);
        }
        return new Array(length).fill("");
    });

    useEffect(() => {
        ref.current[0].focus();
    }, []);

    const isCharacterInvalid = (_key: string) => {
        if (_key.length !== 1) {
            return;
        }

        const key = _key.toLowerCase();

        const isLetter = key >= "a" && key <= "z";
        const isNumber = key >= "0" && key <= "9";

        if (key === " " || key === "-" || (!isLetter && !isNumber)) return false;
        if (!anyCharacter && !isNumber) return false;

        return true;
    };

    const updateCode = (index: number, value: string) => {
        setCode(c => {
            const newCode = [...c];
            newCode[index] = value;
            onChange(newCode.join(""));
            return newCode;
        });
    };

    const typeIn = (index: number, value: string) => {
        updateCode(index, value);
        if (index + 1 < length) {
            ref.current[index + 1].focus();
        } else {
            ref.current[index].blur();
        }
    };

    const backspaceOut = (index: number) => {
        updateCode(index, "");
        if (index - 1 >= 0) {
            ref.current[index - 1].focus();
        }
    };

    const pasteIn = (_, value: string) => {
        // we don't care about the index, we just want to fill in the full code regardless of the index
        for (let i = 0, j = 0; i < length && j < value.length; i++, j++) {
            const isValid = isCharacterInvalid(value[j]);

            if (!isValid) {
                continue;
            }

            updateCode(i, value[j].toLowerCase());
            ref.current[i].focus();
        }
    };

    return (
        <>
            {new Array(length).fill(0).map((_, i) => {
                return (
                    <input
                        ref={el => {
                            ref.current[i] = el as HTMLInputElement;
                        }}
                        key={i}
                        value={code[i]}
                        onChange={noop} // `onChange` here to just fix warning
                        onPaste={e => pasteIn(i, e.clipboardData.getData("text/plain"))}
                        type={anyCharacter ? "text" : "tel"}
                        onKeyDown={e => {
                            if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                                return;
                            }

                            e.preventDefault();

                            if (e.key === "Backspace") {
                                return backspaceOut(i);
                            }

                            if (e.shiftKey && e.key === "Tab") {
                                return backspaceOut(i);
                            }

                            if (e.key === "Tab") {
                                return typeIn(i, "");
                            }

                            const isValid = isCharacterInvalid(e.key);

                            if (!isValid) {
                                return;
                            }

                            typeIn(i, e.key.toLowerCase());
                        }}
                        onFocus={e => e.target.select()}
                        style={{
                            backgroundColor: "transparent",
                            color: themeColors.neutralBlack,
                            fontSize: "2.8rem",
                            textAlign: "center",
                            fontWeight: "600",
                            fontFamily: "var(--knowt-font-name)",
                            outline: "none",
                            height: "6.7rem",
                            width: "5.5rem",
                            padding: "0.6rem 1.2rem 0.6rem 1.2rem",
                            borderRadius: "1.2rem",
                            border: `2px solid ${themeColors.neutralBlack}`,
                            ...style,
                        }}
                        {...inputProps}
                    />
                );
            })}
        </>
    );
};

export const AuthBirthdayInputWithLabel = ({
    label,
    onChange,
    value,
    disabled,
}: {
    label: string;
    onChange: (date: Date) => void;
    value: Date | null;
    disabled?: boolean;
}) => {
    return (
        <FlexColumn style={{ flex: 1 }}>
            <div
                style={{
                    fontSize: "1.6rem",
                    fontWeight: "600",
                    lineHeight: "2.4rem",
                    marginBottom: "1rem",
                }}>
                {label}
            </div>
            <BorderedInputDatePicker
                placeholder="Pick your date of birth"
                disabled={disabled}
                value={value}
                DatePickerProps={{
                    disableFuture: true,
                }}
                onChange={newDate => onChange(newDate.toDate())}
            />
        </FlexColumn>
    );
};

export const AuthAccountTypeInputWithLabel = ({
    value,
    disabled,
    onChange,
    label,
}: {
    value: AccountType | null;
    label: string;
    disabled?: boolean;
    onChange: (value: AccountType) => void;
}) => {
    return (
        <FlexColumn style={{ flex: 1 }}>
            <div
                style={{
                    fontSize: "1.6rem",
                    fontWeight: "600",
                    lineHeight: "2.4rem",
                    marginBottom: "1rem",
                }}>
                {label}
            </div>
            <Select
                disabled={disabled}
                renderLabel={selected => {
                    if (!selected?.value) {
                        return "Select an account type";
                    }
                    return selected?.label;
                }}
                selected={value ? { label: value, value } : null}
                onChange={o => onChange(o.value as AccountType)}
                transitionOrigin={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                }}
                btnSx={{
                    width: "100%",
                    height: "4.8rem",
                    border: `1.5px solid ${themeColors.neutralBlack}`,
                    borderRadius: 999,
                    justifyContent: "space-between",
                    "&:hover": {
                        backgroundColor: themeColors.neutralBlack,
                        color: themeColors.neutralWhite,
                        "> svg": {
                            fill: themeColors.neutralWhite,
                            color: themeColors.neutralWhite,
                        },
                    },
                }}
                menuSx={{ width: "20rem", padding: `${spacing.XS_2} 0` }}
                options={[
                    { label: AccountType.Student, value: AccountType.Student },
                    { label: AccountType.Teacher, value: AccountType.Teacher },
                ]}
            />
        </FlexColumn>
    );
};

export const AuthForm = ({ onSubmit, children, style, ...props }: React.HTMLAttributes<HTMLFormElement>) => {
    return (
        <form
            style={{ width: "100%", margin: "0 auto", ...style }}
            onSubmit={e => {
                e.preventDefault();
                onSubmit?.(e);
            }}
            {...props}>
            {children}
        </form>
    );
};

export const AuthSubmitButton = ({
    children,
    style,
    authStatus,
}: {
    authStatus: "loading" | "success" | "failure" | null;
    // we don't need `onClick` because we expect it's being handled by the form's `onSubmit`
} & Omit<React.HTMLAttributes<HTMLButtonElement>, "onClick">) => {
    const isAuthLoading = authStatus === "loading";
    const isAuthSuccess = authStatus === "success";
    const isAuthFailure = authStatus === "failure";

    return (
        <div style={{ position: "relative" }}>
            {!isAuthFailure && !isAuthSuccess && (
                <Zoom in={true}>
                    <div>
                        <CircularRectTextButton
                            disabled={isAuthLoading}
                            type="submit"
                            sx={{
                                backgroundColor: themeColors.primary,
                                padding: "0.8rem 1.6rem",
                                fontSize: "1.4rem",
                                color: themeColors.primaryDark,
                                height: "4rem",
                                width: "100%",
                                borderRadius: "2rem",
                                ...style,
                            }}>
                            {isAuthLoading ? <LoadingAnimation color={themeColors.neutralWhite} size={28} /> : children}
                        </CircularRectTextButton>
                    </div>
                </Zoom>
            )}

            {(isAuthSuccess || isAuthFailure) && (
                <Zoom in={true} style={{ margin: "0 auto" }} unmountOnExit mountOnEnter>
                    <FlexColumnAlignJustifyCenter>
                        <Circular style={{ backgroundColor: "#50d2c2" }} radius="4.7rem">
                            {isAuthSuccess ? (
                                <Check
                                    strokeWidth={strokeWidth.bold}
                                    size={iconSizes.MD}
                                    color={themeColors.neutralWhite}
                                />
                            ) : (
                                <CircleAlert
                                    strokeWidth={strokeWidth.bold}
                                    size={iconSizes.MD}
                                    color={themeColors.neutralWhite}
                                />
                            )}
                        </Circular>
                    </FlexColumnAlignJustifyCenter>
                </Zoom>
            )}
        </div>
    );
};
